import { Injectable, NgZone } from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';
import { NGXLogger } from 'ngx-logger';
import { environment } from 'src/environments/environment';
import { JwtService } from './jwt.service';

@Injectable({
  providedIn: 'root'
})
export class AmplitudeService {

  private initialized = false;
  private previousPageLocation = '';

  constructor(
    private ngZone: NgZone,
    private jwtService: JwtService,
    private logger: NGXLogger
  ) { }

  init(apiKey: string): void {
    if (this.initialized || !apiKey || !environment.amplitudeEnabled) {
      return;
    }

    const userId = this.jwtService.userId;
    const customerKey = this.jwtService.customerKey;

    this.ngZone.runOutsideAngular(() => {
      const logLevel = !environment.production ? amplitude.Types.LogLevel.Debug : amplitude.Types.LogLevel.None;
      amplitude.init(apiKey, userId, {
        serverZone: 'EU',
        logLevel,
        serverUrl: environment.amplitudeServerUrl,
        autocapture: {
          pageViews: false,
          sessions: true,
          formInteractions: true
        }
      });

      if (customerKey) {
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set('customerKey', customerKey);
        amplitude.identify(identifyEvent);
      }
    });

    this.initialized = true;
    this.logger.trace('Amplitude initialized', { userId, customerKey });
  }

  trackPageView(): void {
    if (!this.initialized) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const currentLocation = window.location.href;
      amplitude.track('[Amplitude] Page Viewed', {
        '[Amplitude] Page Domain': window.location.hostname,
        '[Amplitude] Page Location': currentLocation,
        '[Amplitude] Page Path': window.location.pathname,
        '[Amplitude] Page Title': document.title,
        '[Amplitude] Page URL': currentLocation,
        ...(this.previousPageLocation && {
          '[Amplitude] Previous Page Location': this.previousPageLocation,
          '[Amplitude] Previous Page Type': 'internal',
        }),
      });
      this.previousPageLocation = currentLocation;
    });
  }

  track(eventName: string, eventProperties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      amplitude.track(eventName, eventProperties);
    });
  }

  setUserId(userId: string): void {
    this.ngZone.runOutsideAngular(() => {
      amplitude.setUserId(userId);
    });
  }

  setUserProperties(properties: Record<string, string | number | boolean | string[]>): void {
    this.ngZone.runOutsideAngular(() => {
      const identifyEvent = new amplitude.Identify();
      for (const [key, value] of Object.entries(properties)) {
        identifyEvent.set(key, value);
      }
      amplitude.identify(identifyEvent);
    });
  }

  reset(): void {
    this.ngZone.runOutsideAngular(() => {
      amplitude.reset();
    });
    this.initialized = false;
  }
}
